import { useEffect, useRef, useState } from "react";
import { IMedia, LayoutCommonProps } from "../../../types";
import * as Styled from "./Layout4.styled";
import { ArrowLeft, ArrowRight, ChevronRight } from 'react-bootstrap-icons';
import { BigCard } from "../../media-card/big-card";
import { SidebarCard } from "../../media-card/small-card";
import { SeeCard } from "../../media-card/see-card";
import LayoutModal from "../../media-card/FullScreenModal";
import { scrollOnce, startContinuousScroll, stopContinuousScroll, truncateWords, useResponsiveHeight } from "../../../libs/utils";
import RatingToast from "../RatingToast";

export const Layout4: React.FC<LayoutCommonProps> = ({
    mediaItems,
    mediaRating,
    onWatchClick,
    onRatingSuccess,
    isSignedIn,
    sessionToken,
    activePlans,
    hasFullAccess,
    freeTrialPlanIds,
    playlistTitle,
    enableCompetitionMode,
    enableRatingControls,
    refreshMediaRatings
}) => {
    const rightScrollRef = useRef<HTMLDivElement>(null);
    const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [mainMedia, setMainMedia] = useState<IMedia | undefined>(undefined);
    const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
    const [showToast, setShowToast] = useState(false);

    const handleRatingSuccessLocal = () => {
        setShowToast(true);
        onRatingSuccess();
        refreshMediaRatings?.()
    };

    useEffect(() => {
        if (mediaItems.length && !mainMedia) {
            const firstMedia = mediaItems[0];
            setMainMedia(firstMedia);
            setSelectedCardId(firstMedia.id);
        }
    }, [mediaItems, mainMedia]);


    const handleSeeAllClick = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const onCardClick = (item: IMedia) => {
        setMainMedia(item);
        setSelectedCardId(item.id);
    };

    const responsiveHeight = useResponsiveHeight();

    if (!mediaItems.length || !mainMedia) return null;

    const remainingMedia = mediaItems;


    return (
        <>
            <div>
                {/* First section - existing layout */}
                <Styled.Container>
                    <Styled.MainWrapper>
                        {/* Left section */}
                        <Styled.LeftSection>
                            <Styled.LeftContent>
                                <Styled.TitleSection>
                                    <Styled.TitleWrapper>
                                        <Styled.Title>{playlistTitle}</Styled.Title>
                                        <Styled.Subtitle>({mediaItems?.length} Videos)</Styled.Subtitle>
                                    </Styled.TitleWrapper>

                                    <Styled.SeeAllButton onClick={handleSeeAllClick}>
                                        See all <ChevronRight size={18} />
                                    </Styled.SeeAllButton>
                                </Styled.TitleSection>


                                {/* Arrows - positioned with vertical spacing to align with rating section */}
                                {remainingMedia.length > 4 && (
                                    <Styled.ScrollButtonsWrapper>
                                        <Styled.ScrollButton
                                            onClick={() => scrollOnce(rightScrollRef, "left")}
                                            onMouseDown={() => startContinuousScroll(rightScrollRef, "left", scrollIntervalRef)}
                                            onMouseUp={() => stopContinuousScroll(scrollIntervalRef)}
                                            onMouseLeave={() => stopContinuousScroll(scrollIntervalRef)}
                                            title="Scroll Left"
                                        >
                                            <ArrowLeft size={24} />
                                        </Styled.ScrollButton>
                                        <Styled.ScrollButton
                                            onClick={() => scrollOnce(rightScrollRef, "right")}
                                            onMouseDown={() => startContinuousScroll(rightScrollRef, "right", scrollIntervalRef)}
                                            onMouseUp={() => stopContinuousScroll(scrollIntervalRef)}
                                            onMouseLeave={() => stopContinuousScroll(scrollIntervalRef)}
                                            title="Scroll Right"
                                        >
                                            <ArrowRight size={24} />
                                        </Styled.ScrollButton>
                                    </Styled.ScrollButtonsWrapper>
                                )}
                            </Styled.LeftContent>
                        </Styled.LeftSection>

                        {/* Right section */}
                        <Styled.RightSectionWrapper>
                            <Styled.RightSection>
                                <Styled.BigCardWrapper>
                                    <BigCard
                                        customHeight={responsiveHeight}
                                        customBorderRadius={'0px'}
                                        mediaItem={mainMedia}
                                        mediaRating={mediaRating.find((m) => m.mediaId === mainMedia.mediaId) || 0}
                                        enableRatingControls={enableRatingControls}
                                        isSignedIn={isSignedIn}
                                        hasEligiblePlan={
                                            isSignedIn &&
                                            (!!mainMedia.planDetails?.length
                                                ? !!activePlans.filter((plan) =>
                                                    mainMedia.planDetails?.some((pd) => pd?.id === plan.id)
                                                ).length ||
                                                freeTrialPlanIds.includes(mainMedia.planDetails[0]?.id) ||
                                                hasFullAccess
                                                : true)
                                        }
                                        onWatchClick={onWatchClick}
                                        onRatingSuccess={handleRatingSuccessLocal}
                                        sessionToken={sessionToken}
                                        onlyShowWatchButton={false}
                                        isClickable={false}
                                        disableOverlay={false}
                                        enableCompetitionMode={enableCompetitionMode}
                                    />
                                    <RatingToast show={showToast} onClose={() => setShowToast(false)} />
                                </Styled.BigCardWrapper>

                                <Styled.CardDetailsWrapper>
                                    <Styled.CardTitle>{truncateWords(mainMedia?.title, 100)}</Styled.CardTitle>
                                    {/* <Styled.RatingWrapper>
                                        <Styled.RatingWrapper>
                                            {(() => {
                                                const rating = mediaRating.find((m) => m.mediaId === mainMedia.mediaId)?.rating;
                                                const shouldShowRating = !enableCompetitionMode && (rating || rating === 0);

                                                return shouldShowRating ? (
                                                    <>
                                                        <StarFill color="#000000" />
                                                        <Styled.RatingValue>{rating}/5</Styled.RatingValue>
                                                    </>
                                                ) : null;
                                            })()}
                                        </Styled.RatingWrapper>

                                        <Styled.Director>{mainMedia?.directors}</Styled.Director>
                                    </Styled.RatingWrapper> */}
                                </Styled.CardDetailsWrapper>
                            </Styled.RightSection>
                        </Styled.RightSectionWrapper>
                    </Styled.MainWrapper>

                    <div>
                        {/* Second section - card boxes */}
                        <div>
                            <Styled.ScrollableList ref={rightScrollRef} scrollable={remainingMedia.length > 4}>
                                {remainingMedia.slice(0, 6).map((item) => (
                                    <Styled.SidebarCardWrapper key={item.id}>
                                        <SidebarCard
                                            mediaItem={item}
                                            cardClick={() => onCardClick(item)}
                                            isSelected={item.id === selectedCardId}
                                            mediaRating={mediaRating.find((m) => m.mediaId === item.mediaId) || 0}
                                            enableRatingControls
                                            isSignedIn={isSignedIn}
                                            hasEligiblePlan={isSignedIn && !!item.planDetails?.length}
                                            onWatchClick={onWatchClick}
                                            onRatingSuccess={onRatingSuccess}
                                            sessionToken={sessionToken}
                                        />

                                        {/* Title and Subtitle below the card */}
                                        <Styled.CardTextWrapper>
                                            <Styled.CardTitleText title={item.title}>
                                                {truncateWords(item.title, 20)}
                                            </Styled.CardTitleText>
                                            {/* {(() => {
                                                const rating = mediaRating.find((m) => m.mediaId === item.mediaId)?.rating;
                                                const directors = toTitleCase(item?.directors || "");

                                                const shouldShowRating = !enableCompetitionMode && (rating || rating === 0);

                                                if (!shouldShowRating && !directors) return null;

                                                return (
                                                    <Styled.CardSubtitleWrapper>
                                                        {shouldShowRating && <StarFill color="#000000" />}
                                                        <Styled.CardSubtitleText>
                                                            {shouldShowRating ? `${(rating ?? 0)}/5` : ""}
                                                            {directors && <span>{directors}</span>}
                                                        </Styled.CardSubtitleText>
                                                    </Styled.CardSubtitleWrapper>
                                                );
                                            })()} */}
                                        </Styled.CardTextWrapper>

                                    </Styled.SidebarCardWrapper>
                                ))}

                                {remainingMedia.length > 6 && (
                                    <Styled.SidebarCardWrapper onClick={handleSeeAllClick}>
                                        <SeeCard />
                                    </Styled.SidebarCardWrapper>
                                )}
                            </Styled.ScrollableList>
                        </div>
                    </div>
                </Styled.Container>
            </div>

            {/* Modal with Lazy Loaded Cards */}
            <LayoutModal
                title={playlistTitle}
                show={isModalOpen}
                onHide={handleCloseModal}
                remainingMedia={remainingMedia}
                onCardClick={onCardClick}
                isSignedIn={isSignedIn}
                activePlans={activePlans}
                hasFullAccess={hasFullAccess}
                freeTrialPlanIds={freeTrialPlanIds}
                mediaRating={mediaRating}
                onWatchClick={onWatchClick}
                onRatingSuccess={onRatingSuccess}
                sessionToken={sessionToken}
            />
        </>
    );
};

